import { createSlice } from '@reduxjs/toolkit';
import { initStateReferralGenerate, reducerReferralGenerate } from '../actions/referral/referralGenerate';
import { initStateReferralCheck, reducerReferralCheck } from '../actions/referral/referralCheck';
import { getReferralHistory } from '../actions/referral/getReferralHistory';
import { getReferralLinks } from '../actions/referral/getReferralLinks';
import { createReferralLink } from '../actions/referral/createReferralLink';
import { initStateCalculateReferralPrice, calculateReferralPrice } from '../actions/referral/calculateReferralPrice';
import {
    fetchAdminReferralBonuses,
    resetAdminReferralBonus,
    fetchAdminBonusResetHistory
} from '../actions/referral/referralBonusesActions';

export const initialState = {
  ...initStateReferralGenerate,
  ...initStateReferralCheck,
  ...initStateCalculateReferralPrice,
  referralHistory: {
    data: null,
    loading: false,
    error: null,
  },
  referralLinks: {
    data: null,
    loading: false,
    error: null,
  },
  createLink: {
    loading: false,
    error: null,
  },
  adminReferralBonuses: {
    experts: [],
    loading: false,
    error: null,
    resettingBonus: false,
    resetError: null,
    resetHistory: [],
    resetHistoryLoading: false,
    resetHistoryError: null,
  },
};

export const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    resetReferralGenerate(state, action) {
      state.referralGenerate = initStateReferralGenerate.referralGenerate;
    },
    resetReferralCheck(state, action) {
      state.referralCheck = initStateReferralCheck.referralCheck;
    },
    clearAdminBonusResetError(state) {
      state.adminReferralBonuses.resetError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle referral history states
      .addCase(getReferralHistory.pending, (state) => {
        state.referralHistory.loading = true;
        state.referralHistory.error = null;
      })
      .addCase(getReferralHistory.fulfilled, (state, action) => {
        state.referralHistory.loading = false;
        state.referralHistory.data = action.payload;
      })
      .addCase(getReferralHistory.rejected, (state, action) => {
        state.referralHistory.loading = false;
        state.referralHistory.error = action.payload;
      })
      // Handle referral links states
      .addCase(getReferralLinks.pending, (state) => {
        state.referralLinks.loading = true;
        state.referralLinks.error = null;
      })
      .addCase(getReferralLinks.fulfilled, (state, action) => {
        state.referralLinks.loading = false;
        state.referralLinks.data = action.payload;
      })
      .addCase(getReferralLinks.rejected, (state, action) => {
        state.referralLinks.loading = false;
        state.referralLinks.error = action.payload;
      })
      // Handle create referral link states
      .addCase(createReferralLink.pending, (state) => {
        state.createLink.loading = true;
        state.createLink.error = null;
      })
      .addCase(createReferralLink.fulfilled, (state, action) => {
        state.createLink.loading = false;
        // Add the new link to the existing links array
        if (Array.isArray(state.referralLinks.data)) {
          state.referralLinks.data = [...state.referralLinks.data, action.payload];
        } else {
          state.referralLinks.data = [action.payload];
        }
      })
      .addCase(createReferralLink.rejected, (state, action) => {
        state.createLink.loading = false;
        state.createLink.error = action.payload;
      })
      // Handle calculate referral price states
      .addCase(calculateReferralPrice.pending, (state) => {
        state.calculateReferralPrice.loading = true;
      })
      .addCase(calculateReferralPrice.fulfilled, (state, action) => {
        state.calculateReferralPrice.loading = false;
        state.calculateReferralPrice.data = action.payload;
        state.calculateReferralPrice.error = null;
      })
      .addCase(calculateReferralPrice.rejected, (state, action) => {
        state.calculateReferralPrice.loading = false;
        state.calculateReferralPrice.data = action?.payload;
        state.calculateReferralPrice.error = true;
      })
      // Handle fetchAdminReferralBonuses states
      .addCase(fetchAdminReferralBonuses.pending, (state) => {
        state.adminReferralBonuses.loading = true;
        state.adminReferralBonuses.error = null;
      })
      .addCase(fetchAdminReferralBonuses.fulfilled, (state, action) => {
        state.adminReferralBonuses.loading = false;
        state.adminReferralBonuses.experts = action.payload;
      })
      .addCase(fetchAdminReferralBonuses.rejected, (state, action) => {
        state.adminReferralBonuses.loading = false;
        state.adminReferralBonuses.error = action.payload;
      })
      // Handle resetAdminReferralBonus states
      .addCase(resetAdminReferralBonus.pending, (state) => {
        state.adminReferralBonuses.resettingBonus = true;
        state.adminReferralBonuses.resetError = null;
      })
      .addCase(resetAdminReferralBonus.fulfilled, (state, action) => {
        state.adminReferralBonuses.resettingBonus = false;
      })
      .addCase(resetAdminReferralBonus.rejected, (state, action) => {
        state.adminReferralBonuses.resettingBonus = false;
        state.adminReferralBonuses.resetError = action.payload;
      })
      // Handle fetchAdminBonusResetHistory states
      .addCase(fetchAdminBonusResetHistory.pending, (state) => {
        state.adminReferralBonuses.resetHistoryLoading = true;
        state.adminReferralBonuses.resetHistoryError = null;
      })
      .addCase(fetchAdminBonusResetHistory.fulfilled, (state, action) => {
        state.adminReferralBonuses.resetHistoryLoading = false;
        state.adminReferralBonuses.resetHistory = action.payload;
      })
      .addCase(fetchAdminBonusResetHistory.rejected, (state, action) => {
        state.adminReferralBonuses.resetHistoryLoading = false;
        state.adminReferralBonuses.resetHistoryError = action.payload;
      });
  },
});

export const { resetReferralGenerate, resetReferralCheck, clearAdminBonusResetError } = referralSlice.actions;
export const referralReducer = referralSlice.reducer;
